/**
 * Link Preview Utility for The Deep Wave
 * Fetches Open Graph metadata and creates rich link previews
 */

// Cache for metadata to avoid repeated fetches
const metadataCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Extracts Open Graph and meta tags from HTML content
 */
const extractMetadata = (html, url) => {
    // Create a temporary DOM parser
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    const getMetaContent = (property, attribute = 'property') => {
        const meta = doc.querySelector(`meta[${attribute}="${property}"]`);
        return meta ? meta.getAttribute('content') : null;
    };
    
    const getMetaContentByName = (name) => getMetaContent(name, 'name');
    
    // Extract favicon
    const faviconEl =
        doc.querySelector('link[rel="icon"][type="image/png"]') ||
        doc.querySelector('link[rel="icon"]') ||
        doc.querySelector('link[rel="shortcut icon"]') ||
        doc.querySelector('link[rel="apple-touch-icon"]');
    const faviconHref = faviconEl?.getAttribute('href') || null;
    let favicon = null;
    if (faviconHref) {
        favicon = faviconHref.startsWith('http') ? faviconHref : new URL(faviconHref, new URL(url).origin).href;
    } else {
        favicon = `${new URL(url).origin}/favicon.ico`;
    }

    // Extract all relevant metadata
    const metadata = {
        url: url,
        title: getMetaContent('og:title') ||
               doc.querySelector('title')?.textContent ||
               'Untitled',
        description: getMetaContent('og:description') ||
                    getMetaContentByName('description') ||
                    'No description available',
        image: getMetaContent('og:image') ||
               getMetaContentByName('twitter:image') ||
               null,
        site_name: getMetaContent('og:site_name') || 'TheDeepWave',
        type: getMetaContent('og:type') || 'website',
        author: getMetaContent('og:author') ||
                getMetaContentByName('author') ||
                getMetaContent('article:author'),
        published_time: getMetaContent('og:published_time') ||
                       getMetaContent('article:published_time'),
        modified_time: getMetaContent('og:modified_time') ||
                      getMetaContent('article:modified_time'),
        keywords: getMetaContentByName('keywords'),
        tags: getMetaContent('og:tag') || getMetaContentByName('keywords'),
        robots: getMetaContentByName('robots'),
        // Audio/Video specific
        audio: getMetaContent('og:audio'),
        video: getMetaContent('og:video'),
        duration: getMetaContentByName('duration') ||
                 getMetaContent('video:duration'),
        // Twitter specific
        twitter_card: getMetaContentByName('twitter:card'),
        twitter_title: getMetaContentByName('twitter:title'),
        twitter_description: getMetaContentByName('twitter:description'),
        twitter_image: getMetaContentByName('twitter:image'),
        twitter_creator: getMetaContentByName('twitter:creator'),
        // Additional meta
        theme_color: getMetaContentByName('theme-color'),
        canonical: doc.querySelector('link[rel="canonical"]')?.getAttribute('href'),
        favicon,
    };
    
    // Clean up and format data
    metadata.title = metadata.title.trim();
    metadata.description = metadata.description.trim();
    
    // Ensure image URLs are absolute
    if (metadata.image && !metadata.image.startsWith('http')) {
        const baseUrl = new URL(url).origin;
        metadata.image = new URL(metadata.image, baseUrl).href;
    }
    
    // Parse tags if they exist
    if (metadata.tags) {
        metadata.tags = metadata.tags.split(',').map(tag => tag.trim());
    }
    
    return metadata;
};

/**
 * Fetches metadata for a given URL
 */
export const fetchLinkPreview = async (url) => {
    // Check cache first
    const cacheKey = url;
    const cached = metadataCache.get(cacheKey);
    
    if (cached && (Date.now() - cached.timestamp < CACHE_DURATION)) {
        return cached.data;
    }
    
    try {
        // Use backend preview endpoint to get bot-fetched metadata
        let fetchUrl = url;
        
        // If it's a relative URL, make it absolute
        if (url.startsWith('/')) {
            fetchUrl = `${window.location.origin}${url}`;
        }
        
        const response = await fetch(`/api/preview?url=${encodeURIComponent(fetchUrl)}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
            },
        });
        
        if (response.ok) {
            const result = await response.json();
            if (result.success && result.data) {
                // Cache the result
                metadataCache.set(cacheKey, {
                    data: result.data,
                    timestamp: Date.now()
                });
                return result.data;
            }
        }
        
        // Fallback to direct fetch if preview API fails
        const directResponse = await fetch(fetchUrl, {
            method: 'GET',
            headers: {
                'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8',
            },
        });
        
        if (!directResponse.ok) {
            throw new Error(`HTTP ${directResponse.status}`);
        }
        
        const html = await directResponse.text();
        const metadata = extractMetadata(html, fetchUrl);
        
        // Cache the result
        metadataCache.set(cacheKey, {
            data: metadata,
            timestamp: Date.now()
        });
        
        return metadata;
        
    } catch (error) {
        console.warn(`Failed to fetch metadata for ${url}:`, error);
        
        // Return fallback metadata
        const fallback = createFallbackMetadata(url);
        
        // Cache fallback for a shorter time
        metadataCache.set(cacheKey, {
            data: fallback,
            timestamp: Date.now() - (CACHE_DURATION * 0.8) // Expire sooner
        });
        
        return fallback;
    }
};

/**
 * Creates fallback metadata when fetch fails
 */
const createFallbackMetadata = (url) => {
    // Parse URL to extract meaningful information
    const urlObj = new URL(url.startsWith('http') ? url : `https://thedeepwave.com${url}`);
    const path = urlObj.pathname;
    
    let title = 'TheDeepWave';
    let description = 'Discover amazing music and art on TheDeepWave';
    let type = 'website';
    
    // Generate better fallbacks based on URL patterns
    if (path.startsWith('/profile/')) {
        const username = path.split('/')[2];
        title = `${username} - Artist Profile`;
        description = `Check out ${username}'s profile and content on TheDeepWave`;
        type = 'profile';
    } else if (path.startsWith('/view/audio/')) {
        const parts = path.split('/');
        const filename = parts[4]?.replace(/\.[^/.]+$/, "") || 'Track';
        title = `🎵 ${filename}`;
        description = 'Listen to this amazing track on TheDeepWave';
        type = 'music.song';
    } else if (path.startsWith('/view/video/')) {
        const parts = path.split('/');
        const filename = parts[4]?.replace(/\.[^/.]+$/, "") || 'Video';
        title = `🎬 ${filename}`;
        description = 'Watch this video on TheDeepWave';
        type = 'video.other';
    } else if (path.startsWith('/view/image/')) {
        const parts = path.split('/');
        const filename = parts[4]?.replace(/\.[^/.]+$/, "") || 'Image';
        title = `🖼️ ${filename}`;
        description = 'View this image on TheDeepWave';
        type = 'article';
    } else if (path.startsWith('/view/post/')) {
        title = '📝 Post';
        description = 'Read this post on TheDeepWave';
        type = 'article';
    } else if (path.startsWith('/events/')) {
        title = '🎪 Event';
        description = 'Check out this event on TheDeepWave';
        type = 'article';
    } else if (path === '/feed') {
        title = 'Feed - TheDeepWave';
        description = 'Discover the latest music, videos, and posts from artists';
    } else if (path === '/gallery') {
        title = 'Gallery - TheDeepWave';
        description = 'Explore amazing visual art and media';
    } else if (path === '/community') {
        title = 'The Wave - Artists Directory';
        description = 'Meet the amazing artists in our community';
    } else if (path === '/calendar') {
        title = 'Events Calendar - TheDeepWave';
        description = 'Discover upcoming events and performances';
    }
    
    return {
        url: url,
        title,
        description,
        image: '/android-chrome-512x512.png', // Default site logo
        site_name: 'TheDeepWave',
        type,
        author: null,
        published_time: null,
        modified_time: null,
        tags: null,
        audio: null,
        video: null,
        duration: null,
        twitter_card: 'summary_large_image',
        twitter_creator: '@thedeepwave',
        theme_color: '#4F46E5',
        canonical: url,
        isFallback: true
    };
};

/**
 * Batch fetch metadata for multiple URLs
 */
export const fetchMultiplePreviews = async (urls, { batchSize = 5, delay = 200 } = {}) => {
    const results = new Map();
    
    // Process URLs in batches to avoid overwhelming the server
    for (let i = 0; i < urls.length; i += batchSize) {
        const batch = urls.slice(i, i + batchSize);
        
        const batchPromises = batch.map(async (url) => {
            try {
                const metadata = await fetchLinkPreview(url);
                return { url, metadata, success: true };
            } catch (error) {
                console.warn(`Batch fetch failed for ${url}:`, error);
                return { url, metadata: createFallbackMetadata(url), success: false };
            }
        });
        
        const batchResults = await Promise.all(batchPromises);
        
        batchResults.forEach(({ url, metadata }) => {
            results.set(url, metadata);
        });
        
        // Add delay between batches
        if (i + batchSize < urls.length) {
            await new Promise(resolve => setTimeout(resolve, delay));
        }
    }
    
    return results;
};

/**
 * Clear the metadata cache
 */
export const clearMetadataCache = () => {
    metadataCache.clear();
};

/**
 * Get cache statistics
 */
export const getCacheStats = () => {
    const now = Date.now();
    let validEntries = 0;
    let expiredEntries = 0;
    
    metadataCache.forEach((value) => {
        if (now - value.timestamp < CACHE_DURATION) {
            validEntries++;
        } else {
            expiredEntries++;
        }
    });
    
    return {
        totalEntries: metadataCache.size,
        validEntries,
        expiredEntries,
        cacheHitRate: validEntries / Math.max(metadataCache.size, 1) * 100
    };
};